<!--
  @component Navbar.Menu

  A component that renders a navigation menu for the navbar.
  It appears when the menu is opened and animates in from the specified direction.

  @prop colorVariant - The color variant for the menu background and text
  @prop enterFrom - The direction from which the menu enters ('left' or 'right')
  @prop css - Custom CSS styles to apply to the menu

  Example:
  <Navbar.Menu colorVariant="primary" enterFrom="left">
    <Navbar.MenuGroup label="Main Navigation">
      <Navbar.MenuItem href="/" label="Home" />
      <Navbar.MenuItem href="/products" label="Products" />
      <Navbar.MenuItem href="/about" label="About" />
    </Navbar.MenuGroup>
  </Navbar.Menu>
-->

<script lang="ts">
  import { getContext } from 'svelte';
  import { css } from '@agapi/ui/panda/css';
  import { menuStyles } from '../navbar.styles';
  import { dynamicColorStyles } from '@agapi/ui/panda/utils';
  import { fly } from 'svelte/transition';
  import { cubicOut } from 'svelte/easing';
  import type { NavbarStore } from '../navbar-store.svelte';
  import type { NavbarMenuProps } from '../navbar.types';

  /** Reference to the menu element */
  let menuRef: HTMLElement | null = $state(null);

  /** Props for the Navbar.Menu component */
  const {children, colorVariant, enterFrom = 'left', css: cssProp}: NavbarMenuProps = $props();

  /** Styles for the menu */
  const styles = menuStyles.raw();
  /** Navbar store from context */
  const navbarStore = getContext<NavbarStore>('navbar');
</script>

{#if navbarStore.isMenuOpen}
  <nav
  bind:this={menuRef}
  class={css(styles.root, cssProp)}
  style="
  {colorVariant ? 'background-color: ' + dynamicColorStyles(colorVariant).backgroundColor  + ';' : ''}
  {colorVariant ? 'color: ' + dynamicColorStyles(colorVariant).color + ';' : ''}
  "
  transition:fly={{ duration: 430, x: enterFrom === 'left' ? -menuRef?.clientWidth : -menuRef?.clientWidth, opacity: 1, easing: cubicOut }}
  >
    {#if children}
      {@render children()}
    {/if}
  </nav>
{/if}
